// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Data transformation utilities for inventory overview.
 * Converts raw inventory data into the structure needed for the overview table.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import {
  AssetsInventory,
  GroupedAssetInventoryItem,
  getTotalCount,
} from '@/inventory/api/types';
import { IN_TRANSIT_LOCATION_KEYS } from '@/inventory/components/overview/constants';
import {
  AssetStatusCounts,
  InventoryOverviewData,
  OverviewLocation,
  OverviewProductRow,
  OverviewTotals,
} from '@/inventory/components/overview/types';
import { SparseItem, SparseLocation } from '@/sparse/api/types';

/**
 * Simple maps for tracking data during transformation
 */
interface TransformationMaps {
  /** Products encountered, keyed by product id */
  products: Map<string, SparseItem>;
  /** Locations encountered, keyed by location id */
  locations: Map<string, SparseLocation>;
  /** Asset counts: product key -> location key -> counts */
  productsAtLocationCount: Map<string, Map<string, AssetStatusCounts>>;
  /** In-transit to inventory asset counts per product */
  productsInTransitToInventoryCounts: Map<string, AssetStatusCounts>;
  /** In-transit to non-inventory asset counts per product */
  productsInTransitToNonInventoryCounts: Map<string, AssetStatusCounts>;
  /** Total in-transit asset counts per product */
  productsInTransitCounts: Map<string, AssetStatusCounts>;
}

/**
 * Creates empty asset status counts
 */
function createEmptyAssetCounts(): AssetStatusCounts {
  return {
    FUNCTIONAL: 0,
    NEEDS_REVIEW: 0,
    NON_FUNCTIONAL: 0,
    SCRAPPED: 0,
  };
}

/**
 * Adds a count to the appropriate status bucket
 */
function addToStatusCounts(
  counts: AssetStatusCounts,
  status: AssetFunctionalStatus,
  count = 1
): void {
  counts[status] += count;
}

/**
 * Calculates asset status counts from a list of locations and their corresponding counts.
 * This is a reusable function for efficiently calculating totals across multiple locations.
 */
export function calculateLocationCounts(
  locations: { location: { key: string } }[],
  locationCounts: Record<string, AssetStatusCounts>
): AssetStatusCounts {
  return locations.reduce((totals, loc) => {
    const counts = locationCounts[loc.location.key];
    totals.FUNCTIONAL += counts.FUNCTIONAL || 0;
    totals.NEEDS_REVIEW += counts.NEEDS_REVIEW || 0;
    totals['NON_FUNCTIONAL'] += counts['NON_FUNCTIONAL'] || 0;
    totals.SCRAPPED += counts.SCRAPPED || 0;
    return totals;
  }, createEmptyAssetCounts());
}

/**
 * Initializes transformation maps with empty structures
 */
function createTransformationMaps(): TransformationMaps {
  return {
    products: new Map(),
    locations: new Map(),
    productsAtLocationCount: new Map(),
    productsInTransitToInventoryCounts: new Map(),
    productsInTransitToNonInventoryCounts: new Map(),
    productsInTransitCounts: new Map(),
  };
}

/**
 * Processes a grouped asset item and updates the transformation maps.
 * Grouped assets are already aggregated by product + location + functional status.
 * In-transit assets are included with synthetic location keys.
 */
function processAssetGroup(
  maps: TransformationMaps,
  group: GroupedAssetInventoryItem
): void {
  const productKey = group.item.key;
  const locationKey = group.location.key;

  // Add product if not exists
  if (!maps.products.has(productKey)) {
    maps.products.set(productKey, group.item);
    maps.productsAtLocationCount.set(productKey, new Map());
    maps.productsInTransitToInventoryCounts.set(
      productKey,
      createEmptyAssetCounts()
    );
    maps.productsInTransitToNonInventoryCounts.set(
      productKey,
      createEmptyAssetCounts()
    );
    maps.productsInTransitCounts.set(productKey, createEmptyAssetCounts());
  }

  const status = group.functionalStatus as AssetFunctionalStatus;
  const count = getTotalCount(group.inventory);

  // Handle in-transit special locations - route to in-transit maps, not location maps
  if (locationKey === IN_TRANSIT_LOCATION_KEYS.TO_INVENTORY) {
    const inTransitToInventoryCounts =
      maps.productsInTransitToInventoryCounts.get(productKey);
    const inTransitCounts = maps.productsInTransitCounts.get(productKey);
    if (inTransitToInventoryCounts && inTransitCounts) {
      addToStatusCounts(inTransitToInventoryCounts, status, count);
      addToStatusCounts(inTransitCounts, status, count);
    }
    return;
  }

  if (locationKey === IN_TRANSIT_LOCATION_KEYS.TO_FIELD) {
    const inTransitToNonInventoryCounts =
      maps.productsInTransitToNonInventoryCounts.get(productKey);
    const inTransitCounts = maps.productsInTransitCounts.get(productKey);
    if (inTransitToNonInventoryCounts && inTransitCounts) {
      addToStatusCounts(inTransitToNonInventoryCounts, status, count);
      addToStatusCounts(inTransitCounts, status, count);
    }
    return;
  }

  // Regular location processing
  if (!maps.locations.has(locationKey)) {
    maps.locations.set(locationKey, group.location);
  }

  // Get or create location counts for this product
  const productLocationMap = maps.productsAtLocationCount.get(productKey);
  if (!productLocationMap) return;

  if (!productLocationMap.has(locationKey)) {
    productLocationMap.set(locationKey, createEmptyAssetCounts());
  }

  // Add counts (use the group's count, not 1)
  const locationCounts = productLocationMap.get(locationKey);
  if (!locationCounts) return;
  addToStatusCounts(locationCounts, status, count);
}

/**
 * Builds product rows from transformation maps
 */
function buildProductRows(maps: TransformationMaps): OverviewProductRow[] {
  const allLocationKeys = Array.from(maps.locations.keys());

  // Sort products alphabetically by name
  const sortedProductEntries = Array.from(maps.products.entries()).sort(
    ([, productA], [, productB]) =>
      productA.name.localeCompare(productB.name, undefined, {
        sensitivity: 'base',
      })
  );

  return sortedProductEntries.map(
    ([productKey, product]): OverviewProductRow => {
      const productLocationMap = maps.productsAtLocationCount.get(productKey);
      const inTransitCounts = maps.productsInTransitCounts.get(productKey);
      const inTransitToInventoryCounts =
        maps.productsInTransitToInventoryCounts.get(productKey);
      const inTransitToNonInventoryCounts =
        maps.productsInTransitToNonInventoryCounts.get(productKey);

      if (
        !productLocationMap ||
        !inTransitCounts ||
        !inTransitToInventoryCounts ||
        !inTransitToNonInventoryCounts
      ) {
        throw new Error(`Missing data for product ${productKey}`);
      }

      // Build location counts, ensuring all locations have entries
      const locationCounts: Record<string, AssetStatusCounts> = {};
      allLocationKeys.forEach((locationKey) => {
        const counts = productLocationMap.get(locationKey);
        locationCounts[locationKey] = counts || createEmptyAssetCounts();
      });

      // Calculate totals for this product
      const totalCounts = createEmptyAssetCounts();
      // Sum all values in locationCounts to build the product count
      Object.values(locationCounts).forEach((counts) => {
        totalCounts.FUNCTIONAL += counts.FUNCTIONAL;
        totalCounts['NON_FUNCTIONAL'] += counts['NON_FUNCTIONAL'];
        totalCounts.NEEDS_REVIEW += counts.NEEDS_REVIEW;
        totalCounts.SCRAPPED += counts.SCRAPPED;
      });

      // Add in-transit counts to totals
      totalCounts.FUNCTIONAL += inTransitCounts.FUNCTIONAL;
      totalCounts['NON_FUNCTIONAL'] += inTransitCounts['NON_FUNCTIONAL'];
      totalCounts.NEEDS_REVIEW += inTransitCounts.NEEDS_REVIEW;
      totalCounts.SCRAPPED += inTransitCounts.SCRAPPED;

      return {
        product,
        locationCounts,
        inTransitToInventoryCounts,
        inTransitToNonInventoryCounts,
        inTransitCounts,
        totalCounts,
      };
    }
  );
}

/**
 * Transforms raw inventory data into overview table structure
 */
export function transformInventoryData(
  inventoryData: AssetsInventory
): InventoryOverviewData {
  const maps = createTransformationMaps();

  // Process all grouped asset items to build up maps
  inventoryData.results.forEach((group) => {
    processAssetGroup(maps, group);
  });

  // Convert locations to OverviewLocation format and categorize them
  const inventoryLocations: OverviewLocation[] = [];
  const nonInventoryLocations: OverviewLocation[] = [];

  Array.from(maps.locations.values()).forEach((location) => {
    const overviewLocation: OverviewLocation = {
      location,
      type: location.isInventory ? 'inventory' : 'non-inventory',
      totalCounts: createEmptyAssetCounts(),
    };

    // Calculate total counts for this location across all products
    Array.from(maps.productsAtLocationCount.values()).forEach(
      (productLocationMap) => {
        const locationCounts = productLocationMap.get(location.key);
        if (locationCounts) {
          overviewLocation.totalCounts.FUNCTIONAL += locationCounts.FUNCTIONAL;
          overviewLocation.totalCounts['NON_FUNCTIONAL'] +=
            locationCounts['NON_FUNCTIONAL'];
          overviewLocation.totalCounts.NEEDS_REVIEW +=
            locationCounts.NEEDS_REVIEW;
          overviewLocation.totalCounts.SCRAPPED += locationCounts.SCRAPPED;
        }
      }
    );

    if (location.isInventory) {
      inventoryLocations.push(overviewLocation);
    } else {
      nonInventoryLocations.push(overviewLocation);
    }
  });

  // Sort locations alphabetically within each group
  inventoryLocations.sort((a, b) =>
    a.location.name.localeCompare(b.location.name, undefined, {
      sensitivity: 'base',
    })
  );
  nonInventoryLocations.sort((a, b) =>
    a.location.name.localeCompare(b.location.name, undefined, {
      sensitivity: 'base',
    })
  );

  // Build product rows
  const productRows = buildProductRows(maps);

  // Calculate totals
  const totals = calculateOverviewTotals(
    inventoryLocations,
    nonInventoryLocations,
    productRows
  );

  return {
    inventoryLocations,
    nonInventoryLocations,
    productRows,
    totals,
  };
}

/**
 * Adds counts from source to target
 */
function addCounts(target: AssetStatusCounts, source: AssetStatusCounts): void {
  target.FUNCTIONAL += source.FUNCTIONAL;
  target['NON_FUNCTIONAL'] += source['NON_FUNCTIONAL'];
  target.NEEDS_REVIEW += source.NEEDS_REVIEW;
  target.SCRAPPED += source.SCRAPPED;
}

/**
 * Calculates overview totals from location and product data
 */
function calculateOverviewTotals(
  inventoryLocations: OverviewLocation[],
  nonInventoryLocations: OverviewLocation[],
  productRows: OverviewProductRow[]
): OverviewTotals {
  const inventoryTotal = createEmptyAssetCounts();
  const nonInventoryTotal = createEmptyAssetCounts();
  const inTransitToInventoryTotal = createEmptyAssetCounts();
  const inTransitToNonInventoryTotal = createEmptyAssetCounts();
  const inTransitTotal = createEmptyAssetCounts();

  // Sum inventory location totals
  inventoryLocations.forEach((location) => {
    addCounts(inventoryTotal, location.totalCounts);
  });

  // Sum non-inventory location totals
  nonInventoryLocations.forEach((location) => {
    addCounts(nonInventoryTotal, location.totalCounts);
  });

  // Sum in-transit totals
  productRows.forEach((row) => {
    addCounts(inTransitToInventoryTotal, row.inTransitToInventoryCounts);
    addCounts(inTransitToNonInventoryTotal, row.inTransitToNonInventoryCounts);
    addCounts(inTransitTotal, row.inTransitCounts);
  });

  // Calculate grand total
  const grandTotal = createEmptyAssetCounts();
  addCounts(grandTotal, inventoryTotal);
  addCounts(grandTotal, nonInventoryTotal);
  addCounts(grandTotal, inTransitTotal);

  return {
    inventoryTotal,
    nonInventoryTotal,
    inTransitToInventoryTotal,
    inTransitToNonInventoryTotal,
    inTransitTotal,
    grandTotal,
  };
}
